import {useEffect, useRef} from 'react';

export interface PanelDialogQueueEntry {
    id: string;
    /** Whether the current operator's access level permits this dialog. */
    accessLevelAllowed: boolean;
    /** Whether the operator's personal panel configuration permits this dialog. */
    personalConfigAllowed: boolean;
    /**
     * Called lazily at queue-evaluation time so that the freshest CacheService /
     * localStorage value is read (not a stale closure).
     */
    cacheAllowed: () => boolean;
    /**
     * Opens this dialog. Receives an `onClose` callback that MUST be invoked
     * when the dialog is finished (by any user action), so the queue can advance
     * to the next eligible entry.
     */
    open: (onClose: () => void) => void | Promise<void>;
}

/**
 * Runs a sequential list of panel dialogs, advancing to the next one only after
 * the current dialog calls the `onClose` callback it received.
 *
 * Each entry is evaluated against three eligibility gates before being shown:
 *   1. accessLevelAllowed: operator access-level permission
 *   2. personalConfigAllowed: the operator's personal panel config flag
 *   3. cacheAllowed: a CacheService / localStorage time-based guard
 *
 * Any entry that fails one or more gates is silently skipped.
 *
 * @param entries  Ordered list of dialog queue entries. Build this with `useMemo`
 *                 in the caller so that the array identity is stable across renders.
 * @param ready    While `false` the queue will not start. Flip to `true` once all
 *                 prerequisite async data (e.g. personal panel config) has loaded.
 *                 Defaults to `true`.
 */
export const usePanelDialogQueue = (
    entries: PanelDialogQueueEntry[],
    ready: boolean = true,
) => {
    /*
     * Keep a ref so that `advance` always reads the most recent `entries` array
     * without needing to be captured in the effect's dependency list. This
     * prevents the effect from re-firing when `entries` is rebuilt after renders
     * that happen between the queue starting and a dialog closing.
     */
    const entriesRef = useRef<PanelDialogQueueEntry[]>(entries);
    entriesRef.current = entries;

    /* Guard against running more than once (covers React Strict-Mode double-invoke). */
    const startedRef = useRef(false);

    /**
     * When `ready` becomes true, start iterating through `entries` from the top.
     */
    useEffect(() => {
        if (!ready) return;
        if (startedRef.current) return;
        startedRef.current = true;

        const advance = (fromIndex: number) => {
            const current = entriesRef.current;

            /*
             * Find the first entry at or after `fromIndex` that passes all three
             * eligibility gates. `cacheAllowed` is called here so the CacheService
             * read happens at advancement time, not at queue-build time.
             */
            const sliceRelativeIndex = current
                .slice(fromIndex)
                .findIndex((e) =>
                    e.accessLevelAllowed &&
                    e.personalConfigAllowed &&
                    e.cacheAllowed(),
                );

            if (sliceRelativeIndex === -1) return; // no more eligible dialogs

            const absoluteIndex = fromIndex + sliceRelativeIndex;
            const entry = current[absoluteIndex];

            void entry.open(() => advance(absoluteIndex + 1));
        };

        advance(0);
    }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps
};
